import { CommonModule, Location } from "@angular/common";
import {
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from "@angular/core";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { ActivatedRoute, Router } from "@angular/router";
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  finalize,
  map,
  of,
  shareReplay,
  switchMap,
  tap,
} from "rxjs";
import { AppLayoutComponent } from "../../../shared/components/app-layout/app-layout.component";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { AuthService } from "../../services/auth.service";
import { EventsManagementService } from "../../services/events-management.service";
import { RelationshipsService } from "../../services/relationships.service";
import {
  EventsListFilters,
  EventStatusFilter,
} from "../../services/types/events-management-request.type";
import { EventInfoResponseBody } from "../../services/types/events-management-response.type";
import {
  Organizer,
  OrganizerAddress,
} from "../../services/types/organizer.type";
import { SkeletonModule } from "primeng/skeleton";
import { EventsFilterComponent } from "../../shared/components/events-filter/events-filter.component";
import { EventCardComponent } from "../../shared/components/cards/event-card/event-card.component";
import { ProducersHeaderComponent } from "./header-producers-page/header-producers-page.component";

type ProducerEventsFilters = Omit<EventsListFilters, "page" | "per_page">;

// Holds the paginated state of one event list (available/finished). Both lists
// share the same "Carregar Mais" behaviour, differing only by status.
interface EventListState {
  readonly status: EventStatusFilter;
  readonly events: WritableSignal<EventInfoResponseBody[]>;
  readonly isLoading: WritableSignal<boolean>;
  readonly hasMore: WritableSignal<boolean>;
  loadedPage: number;
}

// Both lists split server-side via the status filter and paginate on demand.
const EVENTS_PER_PAGE = 8;

@Component({
  selector: "app-producers-page",
  imports: [
    CommonModule,
    AppLayoutComponent,
    ProducersHeaderComponent,
    EventsFilterComponent,
    EventCardComponent,
    ButtonComponent,
    SkeletonModule,
  ],
  templateUrl: "./producers-page.component.html",
  styleUrl: "./producers-page.component.scss",
})
export class ProducersPageComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly location = inject(Location);
  private readonly eventsService = inject(EventsManagementService);
  private readonly relationshipsService = inject(RelationshipsService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);

  readonly isFollowed = signal(false);
  readonly isUpdatingFollow = signal(false);
  // True when the logged-in user is viewing their own producer page.
  readonly isOwnProfile = signal(false);
  readonly isLoggedIn = toSignal(this.authService.isLoggedIn, {
    initialValue: false,
  });

  private organizerId: number | null = null;
  private currentFilters: ProducerEventsFilters = {};

  // Available events: active (end_date >= now). Finished events: ended
  // (end_date < now). Both paginate on demand via "Carregar Mais".
  readonly availableList = this.createEventList("active", true);
  readonly finishedList = this.createEventList("ended", false);

  organizer$ = this.route.data.pipe(
    map((data) => data["organizer"] as Organizer),
    shareReplay(1),
  );

  cities$ = this.eventsService.getActiveCities();
  categories$ = this.eventsService.getEventsCategories();

  // Seed filters from the URL so the list reflects shared/bookmarked filters.
  initialFilters: ProducerEventsFilters = this.readFiltersFromUrlSnapshot();
  private readonly filters$ = new BehaviorSubject<ProducerEventsFilters>(
    this.initialFilters,
  );

  private readonly organizerWithFilters$ = combineLatest([
    this.organizer$,
    this.filters$.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
    ),
  ]);

  // Header count reflects the producer's full catalog, independent of filters.
  totalEvents$ = this.organizer$.pipe(
    switchMap((organizer) =>
      this.eventsService.getEventsByOrganizer(organizer.id, { per_page: 1 }),
    ),
    map((page) => page.meta.total_count),
    catchError(() => of(0)),
    shareReplay(1),
  );

  ngOnInit(): void {
    this.subscribeToOrganizer();
    this.subscribeToEventLists();
  }

  handleFiltersChange(filters: ProducerEventsFilters): void {
    // Skip duplicate emissions (e.g. the filter component's initial event).
    if (JSON.stringify(filters) === JSON.stringify(this.filters$.value)) {
      return;
    }

    this.filters$.next(filters);
    this.writeFiltersToUrl(filters);
  }

  loadMoreAvailable(): void {
    this.loadMore(this.availableList);
  }

  loadMoreFinished(): void {
    this.loadMore(this.finishedList);
  }

  private createEventList(
    status: EventStatusFilter,
    initialLoading: boolean,
  ): EventListState {
    return {
      status,
      events: signal<EventInfoResponseBody[]>([]),
      isLoading: signal(initialLoading),
      hasMore: signal(false),
      loadedPage: -1,
    };
  }

  // Reload both lists from page 0 whenever the organizer or filters change.
  private subscribeToEventLists(): void {
    this.organizerWithFilters$
      .pipe(
        tap(([organizer, filters]) => {
          this.organizerId = organizer.id;
          this.currentFilters = filters;
          this.loadEventList(this.availableList, 0, false);
          if (organizer.past_events) {
            this.loadEventList(this.finishedList, 0, false);
          } else {
            this.clearEventList(this.finishedList);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private loadMore(list: EventListState): void {
    if (list.isLoading()) return;
    this.loadEventList(list, list.loadedPage + 1, true);
  }

  private loadEventList(
    list: EventListState,
    page: number,
    append: boolean,
  ): void {
    if (this.organizerId === null) return;

    list.isLoading.set(true);
    // A fresh load (first load or filter change) clears the list so the
    // skeletons show; "Carregar Mais" (append) keeps the current items and
    // shows the loading on the button instead.
    if (!append) {
      list.events.set([]);
      list.hasMore.set(false);
    }

    this.eventsService
      .getEventsByOrganizer(this.organizerId, {
        ...this.currentFilters,
        status: list.status,
        page,
        per_page: EVENTS_PER_PAGE,
      })
      .pipe(
        tap((result) => {
          list.loadedPage = page;
          list.hasMore.set(page + 1 < result.meta.total_pages);
          list.events.update((prev) =>
            append ? [...prev, ...result.data] : result.data,
          );
        }),
        catchError(() => {
          if (!append) list.events.set([]);
          list.hasMore.set(false);
          return EMPTY;
        }),
        finalize(() => list.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private clearEventList(list: EventListState): void {
    list.events.set([]);
    list.hasMore.set(false);
    list.loadedPage = -1;
  }

  private readFiltersFromUrlSnapshot(): ProducerEventsFilters {
    const params = this.route.snapshot.queryParamMap;
    const categoryIdRaw = params.get("category");
    return {
      query: params.get("query") ?? undefined,
      city: params.get("city") ?? undefined,
      start_date: params.get("start_date") ?? undefined,
      end_date: params.get("end_date") ?? undefined,
      category: categoryIdRaw ? Number(categoryIdRaw) : undefined,
    };
  }

  private writeFiltersToUrl(filters: ProducerEventsFilters): void {
    // Update the URL via Location (not Router) so that scroll position
    // restoration doesn't kick in and jump the page back to the top.
    const urlTree = this.router.createUrlTree([], {
      relativeTo: this.route,
      queryParams: {
        query: filters.query || null,
        city: filters.city || null,
        start_date: filters.start_date || null,
        end_date: filters.end_date || null,
        category: filters.category ?? null,
      },
      queryParamsHandling: "merge",
    });

    this.location.replaceState(this.router.serializeUrl(urlTree));
  }

  private subscribeToOrganizer(): void {
    this.organizer$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((organizer) => {
        this.organizerId = organizer.id;
        const currentUserId = this.authService.getLocalStorageUserInfo()?.id;
        this.isOwnProfile.set(currentUserId === organizer.id);
        this.loadFollowStatus(organizer.id);
      });
  }

  private loadFollowStatus(organizerId: number): void {
    // Only logged-in users viewing someone else's page have a follow state.
    if (!this.isLoggedIn() || this.isOwnProfile()) return;

    this.relationshipsService
      .isFollowing(organizerId)
      .subscribe((status) => this.isFollowed.set(status.following));
  }

  toggleFollow(): void {
    if (this.organizerId === null || this.isUpdatingFollow()) return;

    this.isUpdatingFollow.set(true);

    const wasFollowed = this.isFollowed();
    const request$ = wasFollowed
      ? this.relationshipsService.unfollow(this.organizerId)
      : this.relationshipsService.follow(this.organizerId);

    request$
      .pipe(finalize(() => this.isUpdatingFollow.set(false)))
      .subscribe(() => this.isFollowed.set(!wasFollowed));
  }

  formatAddress(address: OrganizerAddress | null): string {
    if (!address) return "";
    const parts = [address.city_name, address.state, address.country].filter(
      Boolean,
    );
    return parts.join(" - ");
  }
}
